import type { AuthUser, UserRole } from '@/stores/authStore';

const VALID_ROLES: readonly UserRole[] = ['admin', 'manager', 'employee', 'customer'];

function isValidRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (VALID_ROLES as readonly string[]).includes(value);
}

/**
 * A role this build does not know must not quietly become a customer. That is how a manager
 * would lose every internal screen without a single error to show for it. We still degrade to
 * `customer` (the safest role) but we say so, loudly, in the console.
 */
function normalizeRole(value: unknown): UserRole {
  if (isValidRole(value)) return value;
  if (value !== undefined && value !== null && value !== '') {
    console.error('[auth] Onbekende rol ontvangen van de API, teruggevallen op klant:', value);
  }
  return 'customer';
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/**
 * Story fu-5 (LIVE BLOCKER, 2026-07-20): normalizes the raw auth-user payload from
 * login/refresh/me into the frontend `AuthUser` contract (apps/web/src/stores/authStore.ts).
 *
 * The API now sends this exact shape (see apps/api/src/modules/auth/auth-user.mapper.ts),
 * but this stays as a defensive boundary — any future drift (a missing field, a malformed
 * array) is normalized here instead of storing a silent `undefined` that later crashes
 * downstream array/string access (`user.glnNumbers[0]` was the ACC crash this guards).
 */
export function toAuthUser(raw: unknown): AuthUser {
  const r = (raw ?? {}) as Partial<AuthUser> & Record<string, unknown>;

  return {
    id: typeof r.id === 'string' ? r.id : String(r.id ?? ''),
    email: typeof r.email === 'string' ? r.email : '',
    name: typeof r.name === 'string' ? r.name : '',
    role: normalizeRole(r.role),
    permissions: toStringArray(r.permissions),
    glnNumbers: toStringArray(r.glnNumbers),
    language: typeof r.language === 'string' ? r.language : 'nl',
    hasChangedPassword: typeof r.hasChangedPassword === 'boolean' ? r.hasChangedPassword : true,
  };
}
